import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { FaArrowLeft, FaPlus, FaEye, FaEyeSlash } from 'react-icons/fa';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { projectsApi } from '../../services/api';
import { useAlert } from '../../contexts/AlertContext';
import ConfirmationModal from '../common/ConfirmationModal';
import Can from '../common/Can';
import { ACTIONS, SUBJECTS } from '../../constants/permissions';
import useProjectsStoreRepository from '../../store/projectsStoreRepository';
import useProjectsRepositorySearch from '../../hooks/useProjectsRepositorySearch';
import useGridNavigation from '../../hooks/useGridNavigation';
import ProjectsRepositoryFilters from './components/ProjectsRepositoryFilters';
import ProjectsGrid from './components/ProjectsGrid';
import ProjectForm from './ProjectForm';
import ResultsCount from '../common/ResultsCount';
import {
  Container,
  Header,
  HeaderLeft,
  BackButton,
  Title,
  HeaderButton,
  Content,
  ControlsContainer,
  MainContainer,
} from './styles';

const ProjectsManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();
  const { id } = useParams();

  const [showControls, setShowControls] = useState(true);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const {
    searchInputValue,
    setSearchInputValue,
    selectedBusinessAreas,
    setSelectedBusinessAreas,
    selectedClients,
    setSelectedClients,
    selectedBusinessUnits,
    setSelectedBusinessUnits,
    selectedProjectStatus,
    setSelectedProjectStatus,
    selectedCluster,
    setSelectedCluster,
    selectedClientPartyTypes,
    setSelectedClientPartyTypes,
    selectedScopeTypes,
    setSelectedScopeTypes,
    selectedManager,
    setSelectedManager,
    includeHierarchyProjects,
    setIncludeHierarchyProjects,
    accessibleProjectIds,
    isLoadingAccessibleProjects,
    clearSearchFilters
  } = useProjectsRepositorySearch();

  const gridRef = useRef(null);

  // Get state and actions from Repository store
  const {
    projects,
    isLoading,
    searchQuery,
    fetchProjects,
    updateProject,
    removeProject,
    setIsReturningFromDetails,
    resetState,
    resetFilters,
  } = useProjectsStoreRepository();

  // Client-side filtering (since API returns all projects)
  // IMPORTANT: This must come BEFORE useGridNavigation so we pass filtered items to the hook
  const filteredProjects = useMemo(() => {
    return projects.filter(project => {
      const matchesSearch = (project.name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
                           (project.code?.toLowerCase() || '').includes(searchQuery.toLowerCase());
      const matchesBusinessAreas = selectedBusinessAreas.length === 0 ||
                                  (project.businessArea && selectedBusinessAreas.includes(project.businessArea.id));
      const matchesClients = selectedClients.length === 0 ||
                            (project.client && selectedClients.includes(project.client.id));
      const matchesBusinessUnits = selectedBusinessUnits.length === 0 ||
                                  (project.businessUnit && selectedBusinessUnits.includes(project.businessUnit.id));
      const matchesStatus = selectedProjectStatus.length === 0 ||
                           selectedProjectStatus.includes(project.status);
      const matchesCluster = !selectedCluster ||
                            (project.businessUnit?.cluster && project.businessUnit.cluster.id === selectedCluster);
      const matchesClientPartyType = selectedClientPartyTypes.length === 0 ||
                                    (project.client?.partyType && selectedClientPartyTypes.includes(project.client.partyType));
      const matchesScopeType = selectedScopeTypes.length === 0 ||
                              (project.scope && selectedScopeTypes.includes(project.scope));

      // Manager filter with hierarchy support
      let matchesManager;
      if (!selectedManager) {
        matchesManager = true;
      } else if (includeHierarchyProjects && accessibleProjectIds) {
        matchesManager = accessibleProjectIds.has(project.id);
      } else {
        matchesManager = project.manager && project.manager.id === selectedManager.id;
      }

      return matchesSearch && matchesBusinessAreas && matchesClients && matchesBusinessUnits && matchesStatus && matchesCluster && matchesClientPartyType && matchesScopeType && matchesManager;
    });
  }, [projects, searchQuery, selectedBusinessAreas, selectedClients, selectedBusinessUnits, selectedProjectStatus, selectedCluster, selectedClientPartyTypes, selectedScopeTypes, selectedManager, includeHierarchyProjects, accessibleProjectIds]);

  // Callback to clean up filters when navigating away from Projects Repository
  const handleNavigateAway = useCallback(() => {
    clearSearchFilters();
    resetFilters();
  }, [clearSearchFilters, resetFilters]);

  // Use the GridNavigation hook with repository routes
  // CRITICAL: Pass filteredProjects, not projects, so indices match what's rendered
  const {
    showLoadingSpinner,
    handleCombinedGridScroll,
    handleItemClick,
    handleBackToItems,
    handleAddNewItem,
    handleCustomBack,
    shouldHideContent,
    isNewItem,
    shouldLoadData,
    shouldForceReload,
    isReturningFromDetailPage,
    navigateWithScrollState,
  } = useGridNavigation({
    gridRef,
    items: filteredProjects, // Use filtered items so indices match
    columnCount: 3,
    rowHeight: 400,
    routes: {
      list: '/projects',
      detail: (id) => `/projects/${id}/edit`,
      new: '/projects/new',
      additionalRoutes: [
        { path: '/view', shouldLoadData: false },
        { path: '/manage-employees', shouldLoadData: false },
        { path: '/manage-code', shouldLoadData: false }
      ]
    },
    onReturnFromDetail: () => {
      setIsReturningFromDetails(true);
    },
    loadMoreDataCallback: null, // No pagination for projects
    pageInfo: { hasNextPage: false },
    isLoadingMore: false,
    onNavigateAway: handleNavigateAway
  });

  // First effect: Always reset the store on mount
  useEffect(() => {
    resetState();
  }, [resetState]);

  // Second effect: Load data when needed
  useEffect(() => {
    // Only run this effect when we're on the main repository view
    // using the centralized shouldLoadData function
    if (!shouldLoadData()) {
      return;
    }

    // CRITICAL FIX: Prevent reloading data if we already have items
    // This prevents data reload when returning from detail view
    // NOTE: We check projects.length (unfiltered) not hasItems (filtered)
    // because we use client-side filtering and hasItems could be 0 due to filters
    if (projects.length > 0 && !shouldForceReload()) {
      return;
    }

    // Fetch all projects
    fetchProjects();
  }, [fetchProjects, shouldLoadData, shouldForceReload, isReturningFromDetailPage, projects.length]);

  // Handle deletion
  const handleDelete = async () => {
    if (!itemToDelete) return;

    try {
      await projectsApi.deleteProject(itemToDelete.id);
      removeProject(itemToDelete.id);
      showAlert(`Project "${itemToDelete.name}" deleted successfully`, 'success');
    } catch (err) {
      showAlert(err.message || 'Failed to delete project', 'error', err);
    } finally {
      setShowDeleteConfirmation(false);
      setItemToDelete(null);
    }
  };

  // Handler functions for ProjectCardItem
  const handleViewClick = (project) => {
    navigateWithScrollState(project, `/projects/${project.id}/view`);
  };

  const handleEditClick = (project) => {
    handleItemClick(project);
  };

  const handleManageEmployeesClick = (project) => {
    navigateWithScrollState(project, `/projects/${project.id}/manage-employees`);
  };

  const handleManageCodeClick = (project) => {
    navigateWithScrollState(project, `/projects/${project.id}/manage-code`);
  };

  const handleDeleteClick = (project) => {
    setItemToDelete(project);
    setShowDeleteConfirmation(true);
  };

  // Render filters section
  const renderFilters = () => {
    return <ProjectsRepositoryFilters
      searchInputValue={searchInputValue}
      setSearchInputValue={setSearchInputValue}
      selectedBusinessAreas={selectedBusinessAreas}
      setSelectedBusinessAreas={setSelectedBusinessAreas}
      selectedClients={selectedClients}
      setSelectedClients={setSelectedClients}
      selectedBusinessUnits={selectedBusinessUnits}
      setSelectedBusinessUnits={setSelectedBusinessUnits}
      selectedProjectStatus={selectedProjectStatus}
      setSelectedProjectStatus={setSelectedProjectStatus}
      selectedCluster={selectedCluster}
      setSelectedCluster={setSelectedCluster}
      selectedClientPartyTypes={selectedClientPartyTypes}
      setSelectedClientPartyTypes={setSelectedClientPartyTypes}
      selectedScopeTypes={selectedScopeTypes}
      setSelectedScopeTypes={setSelectedScopeTypes}
      selectedManager={selectedManager}
      setSelectedManager={setSelectedManager}
      includeHierarchyProjects={includeHierarchyProjects}
      setIncludeHierarchyProjects={setIncludeHierarchyProjects}
      isLoadingAccessibleProjects={isLoadingAccessibleProjects}
    />;
  };

  // Render projects grid
  const renderProjects = () => {
    return (
      <ProjectsGrid
        projects={filteredProjects}
        isLoading={isLoading}
        handleItemClick={handleEditClick}
        handleGridScroll={handleCombinedGridScroll}
        showLoadingSpinner={showLoadingSpinner}
        shouldHideContent={shouldHideContent}
        gridRef={gridRef}
        onView={handleViewClick}
        onEdit={handleEditClick}
        onManageEmployees={handleManageEmployeesClick}
        onManageCode={handleManageCodeClick}
        onDelete={handleDeleteClick}
      />
    );
  };

  // Refresh project after edit (called from ProjectForm)
  const refreshProject = async (projectId) => {
    try {
      const updatedProject = await projectsApi.getProjectById(projectId);
      updateProject(updatedProject);
    } catch (err) {
      showAlert(err.message || 'Failed to refresh project', 'error', err);
    }
  };

  // If showing the Add Project form or Project detail, render the ProjectForm component
  if (id || isNewItem) {
    const scrollState = location.state?.scrollState || location.state?.originalScrollState;
    const backHandler = isNewItem ? handleCustomBack : handleBackToItems;

    // Determine the mode and readOnly based on the path
    let mode = 'edit';
    let readOnly = false;

    if (location.pathname.includes('/view')) {
      mode = 'edit';
      readOnly = true;
    } else if (location.pathname.includes('/manage-employees')) {
      mode = 'manage-employees';
    } else if (location.pathname.includes('/manage-code')) {
      mode = 'manage-code';
    } else if (isNewItem) {
      mode = 'add';
    }

    return (
      <ProjectForm
        projectId={id}
        mode={mode}
        readOnly={readOnly}
        onBack={backHandler}
        onProjectUpdate={id ? () => refreshProject(id) : null}
        scrollState={scrollState}
      />
    );
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/')}>
            <FaArrowLeft /> Back to Home
          </BackButton>
          <Title>Projects Repository</Title>
        </HeaderLeft>
        <div style={{ display: 'flex', gap: '16px' }}>
          <Can I={ACTIONS.CREATE} a={SUBJECTS.PROJECT}>
            <HeaderButton onClick={handleAddNewItem}>
              <FaPlus /> Add Project
            </HeaderButton>
          </Can>
          <HeaderButton onClick={() => setShowControls(!showControls)}>
            {showControls ? <FaEyeSlash /> : <FaEye />}
            {showControls ? 'Hide Panels' : 'Show Panels'}
          </HeaderButton>
        </div>
      </Header>

      <Content>
        <ControlsContainer $show={showControls}>
          {renderFilters()}
        </ControlsContainer>

        <MainContainer $show={showControls}>
          <ResultsCount count={filteredProjects.length} singular="project" plural="projects" />

          {renderProjects()}
        </MainContainer>
      </Content>

      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => {
          setShowDeleteConfirmation(false);
          setItemToDelete(null);
        }}
        onConfirm={handleDelete}
        title="Delete Project"
        message={
          `Are you sure you want to delete "${itemToDelete?.name}"? This action cannot be undone.`
        }
      />
    </Container>
  );
};

export default ProjectsManagement;
